// ============= 直接导出 =============

export type { ILayer } from './CADModule/model/layer/Layer'

// ============= 命名空间形式的导出 =============
// container/types 只导出 TYPES：这里只 import 一次，顶层与 namespace 都复用该绑定。
// 不能再用 `export * / export {} from "./container/types"`——bundleless 输出下
// 「同模块既被 re-export-from 又被 import」会丢失 import 绑定（产物里变成未定义裸引用）。
import { TYPES as _CAD_TYPES } from './container/types'

export { _CAD_TYPES as TYPES }

import {
  configureTextBuilder as _configureTextBuilder,
  preloadFont as _preloadFont,
} from 'troika-three-text'
import * as _CaptureHelper from './CADModule/auxiliaryTool/captureHelper'
import * as _OrthogonalHelper from './CADModule/auxiliaryTool/OrthogonalHelper'
import * as _CADController from './CADModule/CADController'
import * as _AddEntityCommand from './CADModule/command/AddEntityCommand'
import * as _LayerCommand from './CADModule/command/LayerCommand'
import * as _UpdateEntityCommand from './CADModule/command/UpdateEntityCommand'
import * as _DrawAngularDimensionTool from './CADModule/draw/drawAngularDimensionTool'
import * as _DrawArcTool from './CADModule/draw/drawArcTool'
import * as _DrawCircleTool from './CADModule/draw/drawCircleTool'
import * as _DrawMLeaderTool from './CADModule/draw/drawMLeaderTool'
import * as _DrawPolylineTool from './CADModule/draw/drawPolylineTool'
import * as _DrawRectTool from './CADModule/draw/drawRectTool'
import * as _CopyEntityTool from './CADModule/edit/copyEntityTool'
import * as _EntityEditHandler from './CADModule/edit/EntityEditHandler'
import * as _JoinEntityTool from './CADModule/edit/joinEntityTool'
import * as _MoveEntityTool from './CADModule/edit/moveEntityTool'
import * as _CADViewManager from './CADModule/manager/CADViewManager'
import * as _DataManager from './CADModule/manager/DataManager'
import * as _HistoryManager from './CADModule/manager/HistoryManager'
import * as _ObjectManager from './CADModule/manager/ObjectManager'
import * as _ModelConstants from './CADModule/model/common/constant'
import * as _Entity from './CADModule/model/entity'
import * as _Layer from './CADModule/model/layer/Layer'
import * as _MaterialUtils from './CADModule/object/material/MaterialUtils'
import * as _CADToolDispatcher from './CADModule/toolDispatcher/CADToolDispatcher'
import * as _ToolDispatcher from './CADModule/toolDispatcher/interface'
import * as _Setting from './common/setting'
import { createCadContainer as _createCadContainer } from './container/container'

// 复用上面的 import 本地绑定重导出 createCadContainer，规避「同模块 import + export-from」
export { _createCadContainer as createCadContainer }

import * as _Capturer from './core/auxiliaryTool/Capturer'
import type {
  DxfExporterOptions as _DxfExporterOptions,
  DxfFrameBuilderOptions as _DxfFrameBuilderOptions,
  FrameImageBuildArtifacts as _FrameImageBuildArtifacts,
} from './exporter'
import * as _Exporter from './exporter'
import {
  CadI18nService as _CadI18nService,
  type CadTranslator as _CadTranslator,
} from './i18n/CadI18nService'
import { globalCadI18n as _globalI18n } from './i18n/globalI18n'
import { CadTipKey as _CadTipKey } from './i18n/tipKeys'
import * as _Parser from './parser'

export namespace CAD {
  // Types
  export const TYPES = _CAD_TYPES

  // Auxiliary Tools
  export const OrthogonalHelper = _OrthogonalHelper.OrthogonalHelper
  export const CaptureHelper = _CaptureHelper.CaptureHelper
  export const CaptureType = _Capturer.CaptureType

  // Managers
  export const CADViewManager = _CADViewManager.CADViewManager
  export const DataManager = _DataManager.DataManager
  export type ICADMetadata = _DataManager.ICADMetadata
  export const DefaultLayerId = _DataManager.DefaultLayerId
  export const createDefaultLayer = _DataManager.createDefaultLayer
  export const ObjectManager = _ObjectManager.ObjectManager
  export const HistoryManager = _HistoryManager.HistoryManager

  // Controller
  export const CADController = _CADController.CADController

  // Draw Tools
  export const DrawPolylineTool = _DrawPolylineTool.DrawPolylineTool
  export const DrawArcTool = _DrawArcTool.DrawArcTool
  export const ArcDrawMode = _DrawArcTool.ArcDrawMode
  export const CircleDrawMode = _DrawCircleTool.CircleDrawMode
  export const RectDrawMode = _DrawRectTool.RectDrawMode
  export const DrawMLeaderTool = _DrawMLeaderTool.DrawMLeaderTool
  export const DrawAngularDimensionTool = _DrawAngularDimensionTool.DrawAngularDimensionTool

  // Edit Tools
  export const CopyEntityTool = _CopyEntityTool.CopyEntityTool
  export const MoveEntityTool = _MoveEntityTool.MoveEntityTool
  export const JoinEntityTool = _JoinEntityTool.JoinEntityTool

  // Tool Dispatcher
  export const DrawToolType = _ToolDispatcher.DrawToolType
  export const EditToolType = _ToolDispatcher.EditToolType
  export type IDrawToolParams = _ToolDispatcher.IDrawToolParams
  export const CADToolDispatcher = _CADToolDispatcher.CADToolDispatcher

  // Container
  export const createCadContainer = _createCadContainer

  // Command
  export const AddEntityCommand = _AddEntityCommand.AddEntityCommand
  export const UpdateEntityCommand = _UpdateEntityCommand.UpdateEntityCommand
  export const CreateLayerCommand = _LayerCommand.CreateLayerCommand
  export const RemoveLayerCommand = _LayerCommand.RemoveLayerCommand
  export const UpdateLayerCommand = _LayerCommand.UpdateLayerCommand

  // Layer
  export const Layer = _Layer.Layer
  export type ILayer = _Layer.ILayer

  // Entity
  export const EntityType = _ModelConstants.EntityType
  export const LineType = _ModelConstants.LineType
  export const ColorMethod = _ModelConstants.ColorMethod
  export type IEntity = _Entity.IEntity
  export const PolylineEntity = _Entity.PolylineEntity
  export const SplineEntity = _Entity.SplineEntity
  export const ArcEntity = _Entity.ArcEntity
  export const CircleEntity = _Entity.CircleEntity
  export const MTextEntity = _Entity.MTextEntity
  export const MLeaderEntity = _Entity.MLeaderEntity
  export const AlignDimensionEntity = _Entity.AlignDimensionEntity
  export const AngularDimensionEntity = _Entity.AngularDimensionEntity
  export const EntityFactory = _Entity.EntityFactory

  // Plane
  export type IPlane = _DataManager.IPlane
  export type IPlaneMetaData = _DataManager.IPlaneMetaData

  // i18n
  export const CadI18nService = _CadI18nService
  export type CadTranslator = _CadTranslator
  export const TipKey = _CadTipKey
  /** 全局 CAD 国际化实例 */
  export const i18n = _globalI18n
  /** 设置自定义翻译器 */
  export function setTranslator(translator?: _CadTranslator) {
    _globalI18n.setTranslator(translator)
  }
  /** 重置为默认翻译器 */
  export function resetTranslator() {
    _globalI18n.setTranslator(undefined)
  }

  // Exporter
  export const DxfExporter = _Exporter.DxfExporter
  export type DxfExporterOptions = _DxfExporterOptions
  export type DxfFrameBuilderOptions = _DxfFrameBuilderOptions
  export type FrameImageBuildArtifacts = _FrameImageBuildArtifacts

  // Parser
  export const DxfParser = _Parser.DxfParser

  // Others
  export const preloadFont = _preloadFont
  export const configureTextBuilder = _configureTextBuilder
  export const EntityEditHandler = _EntityEditHandler.EntityEditHandler
  export const MaterialUtils = _MaterialUtils.MaterialUtils
  export const Setting = _Setting.Setting
}
